import React, { useEffect, useState } from 'react';
import { Button, Checkbox, Icon, Modal } from 'semantic-ui-react';
import { getFireInsuranceConditionsDocuments } from './fire-insurance-conditions';

export interface FireInsuranceConditionsAcceptance {
  sendCertificateByEmail: boolean;
}

interface FireInsuranceConditionsAcceptanceModalProps {
  open: boolean;
  insurerCode: string;
  loading?: boolean;
  disabled?: boolean;
  header?: string;
  content?: string;
  confirmLabel?: string;
  onCancel: () => void;
  onConfirm: (acceptance: FireInsuranceConditionsAcceptance) => void;
}

const FireInsuranceConditionsAcceptanceModal = ({
  open,
  insurerCode,
  loading = false,
  disabled = false,
  header = 'Contratar Seguro Incêndio',
  content = 'Tem certeza que deseja contratar o Seguro Incêndio?',
  confirmLabel = 'Contratar',
  onCancel,
  onConfirm
}: FireInsuranceConditionsAcceptanceModalProps) => {
  const [conditionsAccepted, setConditionsAccepted] = useState<boolean>(true);
  const [sendCertificateByEmail, setSendCertificateByEmail] =
    useState<boolean>(true);
  const [conditionsDocumentsOpen, setConditionsDocumentsOpen] =
    useState<boolean>(false);
  const conditionsDocuments = getFireInsuranceConditionsDocuments(insurerCode);

  useEffect(() => {
    if (open) {
      setConditionsAccepted(true);
      setSendCertificateByEmail(true);
    } else {
      setConditionsDocumentsOpen(false);
    }
  }, [open]);

  return (
    <Modal
      open={open}
      onClose={onCancel}
      size="large"
      closeOnDimmerClick={false}
    >
      <Modal.Header>{header}</Modal.Header>
      <Modal.Content>
        <p>{content}</p>

        <div
          style={{
            border: '1px solid #c9a8ff',
            borderRadius: 6,
            padding: '12px 14px',
            marginBottom: 12,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 12
          }}
        >
          <Checkbox
            checked={conditionsAccepted}
            onChange={(_, data) => setConditionsAccepted(Boolean(data.checked))}
            aria-label="Aceite das Condições Gerais na contratação"
            style={{ marginTop: 2 }}
          />
          <div>
            <strong>
              Aceite das Condições Gerais na contratação
              <span style={{ color: '#db2828' }}>*</span>
            </strong>
            <p style={{ color: '#767676', margin: '4px 0 2px' }}>
              Declaro que li e estou ciente das Condições Gerais do seguro e
              confirmo que o tipo de imóvel e a atividade exercida pelo
              inquilino estão enquadrados nas condições de aceitação e cobertura
              da seguradora.
            </p>
            <button
              type="button"
              onClick={() => setConditionsDocumentsOpen(true)}
              aria-haspopup="dialog"
              style={{
                background: 'none',
                border: 0,
                color: '#2185d0',
                cursor: 'pointer',
                padding: 0,
                font: 'inherit'
              }}
            >
              <Icon name="file alternate outline" /> Ver Condições Gerais
            </button>
          </div>
        </div>

        <div
          style={{
            border: '1px solid #c9a8ff',
            borderRadius: 6,
            padding: '12px 14px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 12
          }}
        >
          <Checkbox
            checked={sendCertificateByEmail}
            onChange={(_, data) =>
              setSendCertificateByEmail(Boolean(data.checked))
            }
            aria-label="Envio automático do certificado e das Condições Gerais ao inquilino (opcional)"
            style={{ marginTop: 2 }}
          />
          <div>
            <strong>
              Envio automático do certificado e das Condições Gerais ao
              inquilino <span style={{ color: '#767676' }}>(Opcional)</span>
            </strong>
            <p style={{ color: '#767676', margin: '4px 0 0' }}>
              O e-mail será enviado imediatamente após a emissão do certificado.
            </p>
          </div>
        </div>

        {!conditionsAccepted && (
          <p style={{ color: '#db2828', margin: '10px 0 0' }}>
            *Para prosseguir é necessário aceitar as condições gerais de
            contratação.
          </p>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onCancel} disabled={loading}>
          Cancelar
        </Button>
        <Button
          color="green"
          onClick={() => onConfirm({ sendCertificateByEmail })}
          disabled={!conditionsAccepted || loading || disabled}
          loading={loading}
        >
          {confirmLabel}
        </Button>
      </Modal.Actions>

      <Modal
        open={conditionsDocumentsOpen}
        onClose={() => setConditionsDocumentsOpen(false)}
        size="small"
      >
        <Modal.Header>
          {conditionsDocuments?.insurerName || 'Condições Gerais'}
        </Modal.Header>
        <Modal.Content>
          <p>
            Selecione o documento referente ao tipo de imóvel contratado.
          </p>
          {conditionsDocuments ? (
            <div style={{ display: 'grid', gap: 12 }}>
              {[
                {
                  label: 'Condições Gerais - Residencial',
                  url: conditionsDocuments.residentialUrl
                },
                {
                  label: 'Condições Gerais - Comercial',
                  url: conditionsDocuments.commercialUrl
                }
              ].map(document => (
                <div key={document.label}>
                  {document.url ? (
                    <a
                      href={document.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Icon name="file alternate outline" /> {document.label}
                    </a>
                  ) : (
                    <span style={{ color: '#767676' }}>
                      <Icon name="file alternate outline" /> {document.label}
                      {' — Documento ainda não disponibilizado.'}
                    </span>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: '#767676' }}>
              Não há documentos cadastrados para esta seguradora no momento.
            </p>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setConditionsDocumentsOpen(false)}>
            Fechar
          </Button>
        </Modal.Actions>
      </Modal>
    </Modal>
  );
};

export default FireInsuranceConditionsAcceptanceModal;
